import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Toaster } from "../components/ui/sonner";
import { confirmPasswordReset } from "../lib/api";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200/80 shadow-soft p-8 text-center">
          <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-50 text-red-600">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <h1 className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
            Missing reset token
          </h1>
          <p className="mt-2 font-figtree text-sm text-[#475569]">
            This page expects a reset link from your email. Please open the
            link from the email we sent you.
          </p>
          <Link
            to="/forgot-password"
            className="mt-6 inline-block rounded-full bg-[#0F172A] hover:bg-[#1E293B] text-white font-figtree text-sm font-medium px-6 py-2.5"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("Passwords don't match.");
    setBusy(true);
    try {
      await confirmPasswordReset(token, pw);
      setDone(true);
      // Auto-redirect after a beat
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
    >
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200/80 shadow-soft p-8">
        {!done ? (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#ECFDF5] text-[#059669]">
              <KeyRound className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
              Set a new password
            </h1>
            <p className="mt-1 font-figtree text-sm text-[#64748B]">
              Pick something you'll remember. Minimum 8 characters.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="font-figtree text-xs text-[#475569]">
                  New password
                </Label>
                <Input
                  data-testid="reset-pw-new"
                  type="password"
                  required
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                />
              </div>
              <div>
                <Label className="font-figtree text-xs text-[#475569]">
                  Confirm new password
                </Label>
                <Input
                  data-testid="reset-pw-confirm"
                  type="password"
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-1 h-11 rounded-xl border-slate-200 focus-visible:ring-[#10B981]"
                />
              </div>
              {error && (
                <div
                  data-testid="reset-error"
                  className="text-xs text-red-700 border border-red-200 bg-red-50 rounded-lg px-3 py-2"
                >
                  {error}
                </div>
              )}
              <Button
                data-testid="reset-submit"
                type="submit"
                disabled={busy || !pw || !pw2}
                className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium py-5"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating…
                  </>
                ) : (
                  "Set new password"
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-done-state">
            <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#ECFDF5] text-[#059669]">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-outfit text-2xl font-bold tracking-tight text-[#0F172A]">
              Password updated
            </h1>
            <p className="mt-2 font-figtree text-sm text-[#475569]">
              You can now sign in with your new password. Redirecting…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
